import {
  EHandbookMedia,
  EMsnMedia,
  Language,
  SlsMfa,
  SlsMfaOptionTypeEnum
} from '@cms-max/medicare-api-sdk'
import { IMedicareCard, BaseUser, UserWithFields, User } from 'models/User'
import nock from 'nock'

const corsHeaders = {
  'access-control-allow-credentials': 'true',
  'access-control-allow-origin': '*'
}

export const defaultMfaResponse: SlsMfa = {
  promptForMfaEnrollment: false,
  configuredOptions: [
    {
      id: '123',
      type: SlsMfaOptionTypeEnum.Email,
      mfaChannel: 's***t@h***.com',
      deactivated: false,
      deactivatedTimestamp: null,
      optedOut: false,
      optedOutTimestamp: null
    }
  ]
}

export const defaultBeneficiaryResponse: BaseUser = {
  mailingAddress: {
    zip: '02050',
    city: 'New York',
    state: 'NY',
    address1: '',
    address2: ''
  },
  firstName: 'Stephanie',
  lastName: 'Flintstone',
  middleName: 'C',
  railroadRetirementBoardBeneficiary: false,
  username: 'fredFlintstone',
  challenges: [
    {
      question: 'What is the name of the first street you lived on?'
    }
  ],
  mfa: defaultMfaResponse,
  hasPartA: true,
  hasPartB: true,
  isImmunosuppressiveDrugCoverage: false,
  isMedicareAdvantage: false,
  eCorrespondenceSummary: {
    ehandbook_correspondence: false,
    emsn_correspondence: false
  }
}

export const defaultAccountSettingsResponse: User = {
  ...defaultBeneficiaryResponse,
  eMsnPreferences: {
    isEmailPreferred: false,
    language: Language.En,
    media: EMsnMedia.Audio
  },
  eHandbookPreferences: {
    isEmailPreferred: false,
    language: Language.En,
    media: EHandbookMedia.CdRom
  },
  email: 'raccoon1900@gmail.com'
}

export const defaultAccountHomeResponse: UserWithFields<'eMsnPreferences'> = {
  ...defaultBeneficiaryResponse,
  hasPartA: true,
  hasPartB: true,
  isImmunosuppressiveDrugCoverage: false,
  isMedicareAdvantage: false
}

export const defaultBeneficiaryCardData: IMedicareCard = {
  mbi: '4YT1TE4NU10',
  firstName: 'Fred',
  middleName: 'Charles',
  lastName: 'Flintstone',
  railroadRetirementBoardBeneficiary: true,
  isImmunosuppressiveDrugCoverage: false,
  partAEffectiveDate: '08/01/2000',
  partBEffectiveDate: '08/01/2000',
  partAEndDate: '',
  partBEndDate: ''
}

export const nockSLSReqToken = (reqToken?: string) => {
  nock(process.env.REACT_APP_SLS_URL!)
    .get('/sso/authorize')
    .query({ lang: 'en', client_id: 'max', no_redirect: 'true' })
    .reply(200, { req_token: reqToken }, corsHeaders)
}

export const nockBeneReqToken = (reqToken: string) => {
  nock(process.env.REACT_APP_CORE_API_URL!)
    .get('/beneficiary/' + reqToken)
    .reply(200, { firstName: 'Stephanie', refToken: 'some-ref-token' })
}

export const nockVerify = ({
  error,
  authenticated
}: {
  error?: boolean
  authenticated?: boolean
}) => {
  const interceptor = nock(process.env.REACT_APP_CORE_API_URL!)
    .defaultReplyHeaders(corsHeaders)
    .get('/verify')

  let statusCode = 200
  if (error) {
    statusCode = 500
  }

  interceptor.reply(statusCode, {
    authenticated
  })
}

export const nockBene = (statusCode: number, overrides = {}) => {
  nock(process.env.REACT_APP_CORE_API_URL!)
    .defaultReplyHeaders(corsHeaders)
    .get('/beneficiary')
    .reply(
      statusCode,
      {
        ...defaultBeneficiaryResponse,
        ...overrides
      },
      {
        'X-max-session': 'max-session-id'
      }
    )
}

export const nockBeneAccountSettings = (statusCode: number, overrides = {}) => {
  nock(process.env.REACT_APP_CORE_API_URL!)
    .defaultReplyHeaders(corsHeaders)
    .get(
      '/beneficiary?fields=email&fields=eHandbookPreferences&fields=eMsnPreferences'
    ) // TODO - use SDK or update to passing a array of key query params
    .reply(
      statusCode,
      {
        ...defaultAccountSettingsResponse,
        ...overrides
      },
      {
        'X-max-session': 'max-session-id'
      }
    )
}

export const nockUserSignin = (overrides = {}) => {
  const reqToken = 'some-req-token'
  nockSLSReqToken(reqToken)
  nockBeneReqToken(reqToken)
  nockVerify({ authenticated: true })
  nockBene(200, { ...defaultBeneficiaryResponse, ...overrides })
  nockBeneAccountSettings(200, {
    ...defaultAccountSettingsResponse,
    ...overrides
  })
}

export const nockInvalidSignin = () => {
  const reqToken = 'some-req-token'
  nockSLSReqToken(undefined)
  nockBeneReqToken(reqToken)
}
